import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.primary.main};
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const SeasonStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => props.status === 'active' && `
    background: ${theme.colors.success.light};
    color: ${theme.colors.success.dark};
  `}
  ${props => props.status === 'completed' && `
    background: ${theme.colors.secondary.light};
    color: ${theme.colors.secondary.dark};
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  padding: 1.5rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  svg {
    width: 2rem;
    height: 2rem;
    color: ${theme.colors.primary.main};
    margin-bottom: 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const StatUnit = styled.span`
  font-size: 1rem;
  font-weight: 400;
  color: ${theme.colors.text.secondary};
  margin-left: 0.5rem;
`;

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${theme.colors.primary.main};
  }
`;

const TripCard = styled(Link)`
  display: flex;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: ${theme.colors.primary.main};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const TripImage = styled.div`
  width: 120px;
  height: 90px;
  background: ${theme.colors.background.card};
  background-image: url(${props => props.src || ''});
  background-size: cover;
  background-position: center;
  border-radius: ${theme.borderRadius.md};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.secondary};
  font-size: 1.5rem;
  margin-right: 1rem;

  ${props => !props.src && `
    &::after {
      content: '📸';
      opacity: 0.3;
    }
  `}
`;

const TripContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TripTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const TripDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
`;

const TripDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${theme.colors.text.secondary};

  svg {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1rem;
    opacity: 0.5;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.background.secondary};
  color: ${props => props.variant === 'primary' ? theme.colors.primary.contrast : theme.colors.text.secondary};
  border: 1px solid ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? theme.colors.primary.dark : theme.colors.background.card};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const SeasonDetailPage = () => {
  const { vesselId, seasonId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState(null);
  const [vessel, setVessel] = useState(null);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSeasonData();
  }, [vesselId, seasonId]);

  const fetchSeasonData = async () => {
    setLoading(true);
    try {
      const seasonResponse = await api.get(`/api/vessel-seasons/vessel/${vesselId}/season/${seasonId}`);
      setSeason(seasonResponse.data.season);

      const vesselResponse = await api.get(`/api/vessels/${vesselId}`);
      setVessel(vesselResponse.data);

      const summaryResponse = await api.get(`/api/vessel-seasons/${seasonId}/summary`);
      const summary = summaryResponse.data.summary || {};
      setStats({
        totalTrips: summary.total_trips || 0,
        totalDistanceKm: summary.total_distance_km || 0,
        totalDistanceNm: summary.total_distance_nm || 0,
        totalFuelLiters: summary.total_fuel_used_liters || 0,
        totalRunHours: summary.total_run_hours ?? summary.total_engine_hours ?? 0,
        avgSpeedKnots: summary.avg_speed_knots || 0,
        avgFuelPerHour: summary.avg_fuel_per_hour || 0,
      });

      const tripsResponse = await api.get(`/api/trips`, {
        params: {
          vessel_id: vesselId,
          start_date: seasonResponse.data.season.start_date,
          end_date: seasonResponse.data.season.end_date,
          limit: 100,
        },
      });
      setTrips(tripsResponse.data.trips || []);
    } catch (error) {
      console.error('Error fetching season data:', error);
      toast.error('Failed to load season data');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSeason = async () => {
    // TODO: Replace with proper modal component
    try {
      await api.put(`/api/vessel-seasons/${seasonId}`, { status: 'completed' });
      toast.success('Season closed and marked as completed!');
      fetchSeasonData();
    } catch (error) {
      console.error('Error closing season:', error);
      toast.error('Failed to close season');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const tripRunMinutes = (trip) => {
    const run = parseInt(trip.run_time_minutes, 10);
    if (run > 0) return run;
    return 0;
  };

  const tripDistanceNm = (trip) => {
    const nm = parseFloat(trip.total_distance_nm);
    if (Number.isFinite(nm) && nm > 0) return nm;
    const km = parseFloat(trip.total_distance_km);
    if (Number.isFinite(km) && km > 0) return km / 1.852;
    return 0;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <Container>
        <EmptyState>
          <BeakerIcon />
          <p>Loading season data...</p>
        </EmptyState>
      </Container>
    );
  }

  if (!season || !vessel) {
    return (
      <Container>
        <EmptyState>
          <DocumentTextIcon />
          <p>Season not found</p>
          <ActionButton onClick={() => navigate(`/vessels/${vesselId}`)}>
            <ArrowLeftIcon />
            Back to Vessel
          </ActionButton>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderLeft>
          <BackButton to={`/vessels/${vesselId}`}>
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Vessel
          </BackButton>
          <div>
            <Title>{season.name} - {vessel.vessel_name}</Title>
            <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginTop: '0.5rem' }}>
              {formatDate(season.start_date)} - {formatDate(season.end_date)}
            </div>
          </div>
        </HeaderLeft>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <SeasonStatus status={season.status}>
            {season.status === 'active' && <CheckCircleIcon className="w-4 h-4" />}
            {season.status}
          </SeasonStatus>
          {season.status === 'active' && (
            <ActionButton variant="primary" onClick={handleCloseSeason}>
              <DocumentTextIcon />
              Close Season & Generate Report
            </ActionButton>
          )}
        </div>
      </Header>

      {/* Statistics */}
      {stats && (
        <StatsGrid>
          <StatCard>
            <MapPinIcon />
            <StatLabel>Total Trips</StatLabel>
            <StatValue>{stats.totalTrips}</StatValue>
          </StatCard>

          <StatCard>
            <ChartBarIcon />
            <StatLabel>Total Distance</StatLabel>
            <StatValue>
              {(stats.totalDistanceNm || 0).toFixed(1)}
              <StatUnit>nm</StatUnit>
            </StatValue>
          </StatCard>

          <StatCard>
            <BeakerIcon />
            <StatLabel>Total Fuel Used</StatLabel>
            <StatValue>
              {(stats.totalFuelLiters || 0).toFixed(0)}
              <StatUnit>L</StatUnit>
            </StatValue>
          </StatCard>

          <StatCard>
            <ClockIcon />
            <StatLabel>Total Run Time</StatLabel>
            <StatValue>
              {(stats.totalRunHours || 0).toFixed(1)}
              <StatUnit>hrs</StatUnit>
            </StatValue>
          </StatCard>

          <StatCard>
            <ChartBarIcon />
            <StatLabel>Avg Speed</StatLabel>
            <StatValue>
              {(stats.avgSpeedKnots || 0).toFixed(1)}
              <StatUnit>kts</StatUnit>
            </StatValue>
          </StatCard>

          <StatCard>
            <BeakerIcon />
            <StatLabel>Avg Fuel/Hour</StatLabel>
            <StatValue>
              {(stats.avgFuelPerHour || 0).toFixed(1)}
              <StatUnit>L/hr</StatUnit>
            </StatValue>
          </StatCard>
        </StatsGrid>
      )}

      {/* Trips List */}
      <Card>
        <CardHeader>
          <CardTitle>
            <MapPinIcon />
            Trips This Season
          </CardTitle>
          <ActionButton onClick={() => navigate('/trips/create')}>
            + New Trip
          </ActionButton>
        </CardHeader>

        {trips.length > 0 ? (
          trips.map((trip) => (
            <TripCard key={trip.id} to={`/trips/${trip.id}`}>
              <TripImage src={trip.primary_image_url} />
              <TripContent>
                <TripTitle>{trip.trip_name}</TripTitle>
                <TripDetails>
                  <TripDetail>
                    <CalendarDaysIcon />
                    {formatDate(trip.start_time)}
                  </TripDetail>
                  <TripDetail>
                    <ClockIcon />
                    {formatDuration(tripRunMinutes(trip))}
                  </TripDetail>
                  <TripDetail>
                    <ChartBarIcon />
                    {tripDistanceNm(trip).toFixed(1)} nm
                  </TripDetail>
                  <TripDetail>
                    <BeakerIcon />
                    {parseFloat(trip.total_fuel_used_liters || 0).toFixed(0)}L
                  </TripDetail>
                </TripDetails>
              </TripContent>
            </TripCard>
          ))
        ) : (
          <EmptyState>
            <MapPinIcon />
            <p>No trips recorded for this season yet</p>
            <ActionButton variant="primary" onClick={() => navigate('/trips/create')}>
              Create Your First Trip
            </ActionButton>
          </EmptyState>
        )}
      </Card>
    </Container>
  );
};

export default SeasonDetailPage;

